import type {
  AssignmentRecord,
  AssignmentsTreeResponse,
  FormType,
  VisitCategory,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";
import type { ProposedAssignmentChange } from "@/api/form-builder/formAssistant.types";

/** One assignment slot: category, visit type and the scope it is narrowed to. */
export interface AssignmentSlot {
  tenantId: string;
  workOrderType: VisitCategory;
  formType: FormType;
  clientId?: string;
  brandId?: string;
  tradeId?: number;
  subtradeId?: number;
}

/** The company's own row in the slot, if one is there; ServEngine defaults are not rows to replace. */
export function findSlotRow(
  tree: AssignmentsTreeResponse,
  slot: AssignmentSlot,
): AssignmentRecord | null {
  const entity =
    tree.entities.find((node) => node.entityId === slot.tenantId) ?? tree.entities[0] ?? null;
  if (!entity) return null;
  const client = slot.clientId
    ? entity.clients.find((group) => group.clientId === slot.clientId)
    : undefined;
  const brand = slot.brandId
    ? client?.brands.find((group) => group.brandId === slot.brandId)
    : undefined;
  if ((slot.clientId && !client) || (slot.brandId && !brand)) return null;
  const categories = brand?.categories ?? client?.categories ?? entity.categories;
  const category = categories.find((node) => node.visitCategory === slot.workOrderType);
  return (
    category?.assignments.find(
      (row) =>
        row.formType === slot.formType &&
        (row.clientId ?? undefined) === slot.clientId &&
        (row.brandId ?? undefined) === slot.brandId &&
        (row.tradeId ?? undefined) === slot.tradeId &&
        (row.subtradeId ?? undefined) === slot.subtradeId,
    ) ?? null
  );
}

/**
 * The change that puts a published form in a slot, in the shape the assignments
 * assistant applies: a replace when the company has a row there, a create when
 * not, nothing when the form is already the one in use.
 */
export function proposeSlotChange(
  tree: AssignmentsTreeResponse,
  slot: AssignmentSlot,
  form: { templateId: string; formName: string; slotLabel: string },
): ProposedAssignmentChange | null {
  const row = findSlotRow(tree, slot);
  if (row?.templateId === form.templateId) return null;
  return {
    key: "assign-published-form",
    action: row ? "replace" : "create",
    rowId: row?.id ?? null,
    assignment: {
      workOrderType: slot.workOrderType,
      formType: slot.formType,
      clientId: slot.clientId ?? null,
      brandId: slot.brandId ?? null,
      tradeId: slot.tradeId ?? null,
      subtradeId: slot.subtradeId ?? null,
      templateId: form.templateId,
    },
    where: form.slotLabel,
    fromTemplateName: row?.templateName ?? null,
    toTemplateName: form.formName,
    warning: null,
    reason: "You asked for this form for this slot.",
  };
}
